// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import { IQaGetListAttrs, IQaDeleteAttrs, IQaGetDetailAttrs } from 'interfaces/back-office/qa/qa-view.interface'
import { IQaApiListResponse, IQaApiDetailResponse, IQaApiNullResponse } from 'interfaces/back-office/qa/qa-api.interface'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withHelper_paginationProps } from 'models/extensions/with-helper'
import { withEnvironment } from 'models/extensions/with-environment'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'

// Constant
import { LOADING } from 'constant/shared.constant'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'

// Lodash
import cloneDeep from 'lodash/cloneDeep'

// Types
const { number, string, model, maybeNull, optional, array } = types

// Loading
const { BASE, DETAIL, ACTION } = LOADING

// Question
const questionProps = {
  id: maybeNull(number),
  number: maybeNull(number),
  respondentEmail: maybeNull(string),
  respondentName: maybeNull(string),
  title: maybeNull(string),
  message: maybeNull(string),
  receiveAt: maybeNull(string),
  replyAt: maybeNull(string),
}

// QnA Model
const QuestionList = optional(array(model('QuestionList').props(questionProps)), [])

// Question Paginated
const QuestionPaginated = model('QuestionPaginated').props({ ...withHelper_paginationProps, list: QuestionList })

// Question Detail
const QuestionDetail = model('QuestionDetail').props(questionProps)

const QaStoreModel = types
  .model('Qa')
  .props({
    qa_loading: withHelper_commonLoadingModel,
    qa_list: optional(QuestionPaginated, {}),
    qa_detail: optional(QuestionDetail, {}),
  })
  .extend(withEnvironment)
  .actions(self => ({
    qa_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.qa_loading, payload)

      detach(self.qa_loading)
      self.qa_loading = cast(map)
    },
    qa_SET_LIST: (payload: IQaApiListResponse['res']['data']): void => {
      const mapPayload = cloneDeep({
        ...payload,
        list: payload.list.map(list => {
          return {
            ...list,
            receiveAt: dateUtils_formatDate(list.receiveAt!, { tableFormat: true }),
            replyAt: dateUtils_formatDate(list.replyAt!, { tableFormat: true }),
          }
        }),
      })

      self.qa_list = cast(mapPayload)
    },
    qa_SET_DETAIL: (payload: IQaApiDetailResponse['res']['data']): void => {
      self.qa_detail = cast(payload)
    },
    qa_CLEAR_LIST: (): void => {
      self.qa_list = cast({})
    },
    qa_CLEAR_DETAIL: (): void => {
      self.qa_detail = cast({})
    },
  }))
  .actions(self => ({
    /**
     * @description Fetch questions
     *
     * @param {IQaGetListAttrs} payload
     *
     * @return {any} any
     */
    qa_fetchList: flow(function* (payload: IQaGetListAttrs) {
      self.qa_SET_LOADING({ type: BASE, value: true })

      const response: IQaApiListResponse = yield self.environment.qaApi.qa_getList({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        self.qa_SET_LIST(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.qa_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Fetch question
     *
     * @param {IQaGetDetailAttrs} payload
     *
     * @return {any} any
     */
    qa_fetchDetail: flow(function* (payload: IQaGetDetailAttrs) {
      self.qa_SET_LOADING({ type: DETAIL, value: true })

      const response: IQaApiDetailResponse = yield self.environment.qaApi.qa_getDetail({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        self.qa_SET_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.qa_SET_LOADING({ type: DETAIL, value: false })
    }),

    /**
     * @description Delete question
     *
     * @param {IQaDeleteAttrs} payload
     *
     * @return {any} any
     */
    qa_delete: flow(function* (payload: IQaDeleteAttrs) {
      self.qa_SET_LOADING({ type: ACTION, value: true })

      const response: IQaApiNullResponse = yield self.environment.qaApi.qa_delete({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.qa_SET_LOADING({ type: ACTION, value: false })
    }),
  }))

export const createQaStoreDefaultModel = () => optional(QaStoreModel, {})
